/**
 * Mutaciones local-first del dominio de cursos (Space).
 *
 * Escriben en la BD PowerSync local (Drizzle) → la cola de sync las envía al
 * servidor (`db/sync.rs`), que aplica authz (autor/editor, CC-40), sella la
 * versión del curso y RE-CORRIGE el progreso (antifraude). El cliente jamás es
 * la autoridad: la nota/XP definitivos llegan de vuelta por sync.
 */

import {
  and,
  block,
  course,
  enrollment,
  eq,
  lesson,
  lesson_progress,
  type PowerSyncDrizzleDB,
  unit,
} from "@space/powersync";
import type { BlockResponse } from "../learning-blocks/types";
import { nowISO } from "../utils/date-helpers";
import { generateUUID } from "../utils/uuid";

// ─── Autoría: curso ──────────────────────────────────────────────────────────

export interface CreateCourseInput {
  title?: string;
  description?: string;
  category?: string;
  language?: string;
}

/** Crea un curso en borrador. El autor es el usuario actual (sync fuerza authz). */
export async function createCourseMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  input: CreateCourseInput = {},
): Promise<string> {
  const id = generateUUID();
  const now = nowISO();
  await db.insert(course).values({
    id,
    author_id: userId,
    title: input.title?.trim() || "Nuevo curso",
    description: input.description ?? null,
    category: input.category ?? null,
    language: input.language ?? "es",
    visibility: "draft",
    version: 1,
    created_at: now,
    updated_at: now,
  });
  return id;
}

export async function updateCourseMutation(
  db: PowerSyncDrizzleDB,
  courseId: string,
  updates: Partial<{
    title: string;
    description: string | null;
    category: string | null;
    language: string;
  }>,
): Promise<void> {
  const patch = { ...updates } as Record<string, unknown>;
  if (typeof patch.title === "string") {
    patch.title = patch.title.trim() || "Nuevo curso";
  }
  await db
    .update(course)
    .set({ ...patch, updated_at: nowISO() })
    .where(eq(course.id, courseId));
}

/** Publica el curso en el catálogo público (guest-first). Sella `published_at`. */
export async function publishCourseMutation(
  db: PowerSyncDrizzleDB,
  courseId: string,
): Promise<void> {
  await db
    .update(course)
    .set({ visibility: "public", updated_at: nowISO() })
    .where(eq(course.id, courseId));
}

/** Vuelve el curso a borrador (lo retira del catálogo). */
export async function unpublishCourseMutation(
  db: PowerSyncDrizzleDB,
  courseId: string,
): Promise<void> {
  await db
    .update(course)
    .set({ visibility: "draft", updated_at: nowISO() })
    .where(eq(course.id, courseId));
}

export async function deleteCourseMutation(
  db: PowerSyncDrizzleDB,
  courseId: string,
): Promise<void> {
  await db
    .update(course)
    .set({ deleted_at: nowISO(), updated_at: nowISO() })
    .where(eq(course.id, courseId));
}

// ─── Autoría: unidad / lección / bloque ──────────────────────────────────────

export async function addUnitMutation(
  db: PowerSyncDrizzleDB,
  courseId: string,
  opts: { title?: string; position?: number } = {},
): Promise<string> {
  const id = generateUUID();
  const now = nowISO();
  await db.insert(unit).values({
    id,
    course_id: courseId,
    title: opts.title?.trim() || "Nueva unidad",
    position: opts.position ?? 0,
    created_at: now,
    updated_at: now,
  });
  return id;
}

export async function updateUnitMutation(
  db: PowerSyncDrizzleDB,
  unitId: string,
  updates: Partial<{ title: string; position: number }>,
): Promise<void> {
  await db
    .update(unit)
    .set({ ...updates, updated_at: nowISO() })
    .where(eq(unit.id, unitId));
}

export async function deleteUnitMutation(db: PowerSyncDrizzleDB, unitId: string): Promise<void> {
  await db
    .update(unit)
    .set({ deleted_at: nowISO(), updated_at: nowISO() })
    .where(eq(unit.id, unitId));
}

export async function addLessonMutation(
  db: PowerSyncDrizzleDB,
  params: {
    courseId: string;
    unitId: string;
    title?: string;
    position?: number;
    xpReward?: number;
  },
): Promise<string> {
  const id = generateUUID();
  const now = nowISO();
  await db.insert(lesson).values({
    id,
    unit_id: params.unitId,
    course_id: params.courseId,
    title: params.title?.trim() || "Nueva lección",
    position: params.position ?? 0,
    xp_reward: params.xpReward ?? 10,
    created_at: now,
    updated_at: now,
  });
  return id;
}

export async function updateLessonMutation(
  db: PowerSyncDrizzleDB,
  lessonId: string,
  updates: Partial<{ title: string; position: number; xp_reward: number }>,
): Promise<void> {
  await db
    .update(lesson)
    .set({ ...updates, updated_at: nowISO() })
    .where(eq(lesson.id, lessonId));
}

export async function deleteLessonMutation(
  db: PowerSyncDrizzleDB,
  lessonId: string,
): Promise<void> {
  await db
    .update(lesson)
    .set({ deleted_at: nowISO(), updated_at: nowISO() })
    .where(eq(lesson.id, lessonId));
}

export async function addBlockMutation(
  db: PowerSyncDrizzleDB,
  params: {
    courseId: string;
    lessonId: string;
    kind: string;
    content: unknown;
    position?: number;
    weight?: number;
  },
): Promise<string> {
  const id = generateUUID();
  const now = nowISO();
  await db.insert(block).values({
    id,
    lesson_id: params.lessonId,
    course_id: params.courseId,
    kind: params.kind,
    position: params.position ?? 0,
    weight: params.weight ?? 1,
    content: JSON.stringify(params.content ?? {}),
    created_at: now,
    updated_at: now,
  });
  return id;
}

export async function updateBlockMutation(
  db: PowerSyncDrizzleDB,
  blockId: string,
  updates: Partial<{ kind: string; content: unknown; position: number; weight: number }>,
): Promise<void> {
  const patch: Record<string, unknown> = { updated_at: nowISO() };
  if (updates.kind !== undefined) patch.kind = updates.kind;
  if (updates.position !== undefined) patch.position = updates.position;
  if (updates.weight !== undefined) patch.weight = updates.weight;
  if (updates.content !== undefined) patch.content = JSON.stringify(updates.content);
  await db.update(block).set(patch).where(eq(block.id, blockId));
}

export async function deleteBlockMutation(db: PowerSyncDrizzleDB, blockId: string): Promise<void> {
  await db
    .update(block)
    .set({ deleted_at: nowISO(), updated_at: nowISO() })
    .where(eq(block.id, blockId));
}

// ─── Consumo: inscripción + progreso ─────────────────────────────────────────

/** Inscribe al usuario en un curso (idempotente por la unique del server). */
export async function enrollMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  courseId: string,
): Promise<string> {
  const id = generateUUID();
  const now = nowISO();
  await db.insert(enrollment).values({
    id,
    user_id: userId,
    course_id: courseId,
    created_at: now,
    updated_at: now,
  });
  return id;
}

export async function unenrollMutation(
  db: PowerSyncDrizzleDB,
  enrollmentId: string,
): Promise<void> {
  await db
    .update(enrollment)
    .set({ deleted_at: nowISO(), updated_at: nowISO() })
    .where(eq(enrollment.id, enrollmentId));
}

export interface SaveLessonProgressInput {
  progressId?: string;
  userId: string;
  courseId: string;
  lessonId: string;
  status: "in_progress" | "completed";
  lastBlockId?: string;
  /** Nota local afirmada (0..1). El servidor la re-verifica; solo informativa. */
  claimedScore?: number;
  responses?: BlockResponse[];
  startedAt?: number;
  finishedAt?: number;
}

/**
 * Persiste el progreso de una lección. Envía las `responses` (JSON string) y la
 * nota afirmada para que el servidor RE-CORRIJA en el write-path y compute el XP
 * autoritativo. Devuelve el id de la fila `lesson_progress` (estable por lección
 * para reanudar). El cliente puede pintar feedback inmediato con
 * `applyLessonCompletion`, pero la nota/XP definitivos llegan por sync.
 */
export async function saveLessonProgressMutation(
  db: PowerSyncDrizzleDB,
  input: SaveLessonProgressInput,
): Promise<string> {
  const now = nowISO();
  // PowerSync expone las tablas locales como VISTAS y NO admite UPSERT
  // (`ON CONFLICT`) sobre ellas → un `.onConflictDoUpdate()` lanza «cannot UPSERT
  // a view» y el progreso NO se escribía (ni local ni sync). Resolvemos la fila
  // ESTABLE por (user_id, lesson_id) y hacemos UPDATE o INSERT manual. Así hay
  // una sola fila por lección (reanudar por last_block_id) sin depender del UPSERT.
  const existing = await db
    .select({ id: lesson_progress.id })
    .from(lesson_progress)
    .where(
      and(eq(lesson_progress.user_id, input.userId), eq(lesson_progress.lesson_id, input.lessonId)),
    )
    .limit(1);
  const id = existing[0]?.id ?? input.progressId ?? generateUUID();
  const values = {
    id,
    user_id: input.userId,
    course_id: input.courseId,
    lesson_id: input.lessonId,
    status: input.status,
    last_block_id: input.lastBlockId ?? null,
    claimed_score: input.claimedScore ?? null,
    responses: input.responses ? JSON.stringify(input.responses) : null,
    started_at: input.startedAt ?? null,
    finished_at: input.finishedAt ?? null,
    updated_at: now,
  };
  if (existing[0]) {
    await db.update(lesson_progress).set(values).where(eq(lesson_progress.id, id));
  } else {
    await db.insert(lesson_progress).values({ ...values, created_at: now });
  }
  return id;
}
